export interface LookMetadata {
  look_name: string;
  look_index: number;
  website_content: {
    look_hook: string;
    look_name: string;
    stylist_note: string;
  };
  image_generation_variables: {
    occasion: string;
    look_name: string;
    sub_occasion: string;
    image_styling: {
      bag: string;
      footwear: string;
      layering: string;
      jewellery: string;
      style_note: string;
      accessories: string;
      hair_and_makeup: string;
      outfit_completion: string;
    };
    model_description: string;
    scene_description: string;
    occasion_description: string;
  };
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value);

const readString = (value: unknown): string => (typeof value === 'string' ? value : '');

const readNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  return 0;
};

export const buildMetadataDefaults = (
  metadata?: Record<string, unknown> | LookMetadata,
): LookMetadata => {
  const record = isRecord(metadata) ? metadata : {};
  const websiteContent = isRecord(record.website_content) ? record.website_content : {};
  const imageVars = isRecord(record.image_generation_variables)
    ? record.image_generation_variables
    : {};
  const imageStyling = isRecord(imageVars.image_styling) ? imageVars.image_styling : {};

  return {
    look_name: readString(record.look_name),
    look_index: readNumber(record.look_index),
    website_content: {
      look_hook: readString(websiteContent.look_hook),
      look_name: readString(websiteContent.look_name),
      stylist_note: readString(websiteContent.stylist_note),
    },
    image_generation_variables: {
      occasion: readString(imageVars.occasion),
      look_name: readString(imageVars.look_name),
      sub_occasion: readString(imageVars.sub_occasion),
      image_styling: {
        bag: readString(imageStyling.bag),
        footwear: readString(imageStyling.footwear),
        layering: readString(imageStyling.layering),
        jewellery: readString(imageStyling.jewellery),
        style_note: readString(imageStyling.style_note),
        accessories: readString(imageStyling.accessories),
        hair_and_makeup: readString(imageStyling.hair_and_makeup),
        outfit_completion: readString(imageStyling.outfit_completion),
      },
      model_description: readString(imageVars.model_description),
      scene_description: readString(imageVars.scene_description),
      occasion_description: readString(imageVars.occasion_description),
    },
  };
};

export const buildLookEditorMetadata = (look?: Record<string, unknown>): LookMetadata => {
  const record = isRecord(look) ? look : {};
  const metadata = isRecord(record.metadata) ? record.metadata : {};
  const websiteContent = isRecord(metadata.website_content) ? metadata.website_content : {};

  return buildMetadataDefaults({
    ...metadata,
    look_name:
      readString(record.look_name) ||
      readString(metadata.look_name) ||
      readString(websiteContent.look_name),
    image_generation_variables:
      (isRecord(record.image_generation_variables)
        ? record.image_generation_variables
        : metadata.image_generation_variables) ?? {},
    website_content: {
      ...websiteContent,
      look_name: readString(record.look_name) || readString(websiteContent.look_name),
      look_hook: readString(record.look_hook) || readString(websiteContent.look_hook),
    },
  });
};

export const normalizeLookMetadata = (metadata?: LookMetadata): LookMetadata | undefined => {
  if (!metadata) {
    return undefined;
  }
  const parsedLookIndex = Number(metadata.look_index);
  const normalizedMetadata = {
    ...metadata,
    look_index: Number.isFinite(parsedLookIndex) ? parsedLookIndex : 0,
    website_content: {
      ...metadata.website_content,
    },
  };
  const restMetadata = Object.fromEntries(
    Object.entries(normalizedMetadata).filter(
      ([key]) => key !== 'look_name' && key !== 'image_generation_variables',
    ),
  ) as Omit<LookMetadata, 'look_name' | 'image_generation_variables'>;
  const restWebsiteContent = Object.fromEntries(
    Object.entries(normalizedMetadata.website_content).filter(
      ([key]) => key !== 'look_name' && key !== 'look_hook',
    ),
  ) as Omit<LookMetadata['website_content'], 'look_name' | 'look_hook'>;

  return {
    ...restMetadata,
    website_content: restWebsiteContent,
  } as LookMetadata;
};
